import { Controller } from '@hotwired/stimulus'

/**
 * Controller para la seccion de configuracion de grabacion en webinars.
 *
 * Gestiona:
 * - Visibilidad de la seccion segun is_youtube_platform
 * - Mostrar/ocultar selector de fecha para opcion "Personalizado"
 * - Validacion frontend de fecha posterior a hora de fin
 *
 * @example
 * <div data-controller="recording-config"
 *      data-recording-config-youtube-value="true"
 *      data-recording-config-end-time-value="2026-02-15T18:00:00">
 *   <div data-recording-config-target="section">...</div>
 *   <input type="datetime-local" data-recording-config-target="customDatetime">
 * </div>
 */
export default class extends Controller {
  static targets = [
    'section',         // Contenedor de toda la seccion de grabacion
    'customDatetime',  // Input datetime-local para fecha personalizada
    'customContainer', // Contenedor del datetime (para show/hide)
    'radioOption',     // Radio buttons de opciones
    'errorMessage'     // Elemento para mostrar errores
  ]

  static values = {
    youtube: Boolean,      // Si la URL actual es de Youtube
    endTime: String,       // Hora de fin del webinar (ISO 8601)
    minDatetimeBuffer: { type: Number, default: 60 } // Minutos minimos despues del fin
  }

  connect() {
    this.updateSectionVisibility()
    this.updateCustomDatetimeVisibility()
    this.setMinDatetime()
  }

  updateSectionVisibility() {
    if (this.hasSectionTarget) {
      this.sectionTarget.hidden = !this.youtubeValue
    }
  }

  youtubeValueChanged() {
    this.updateSectionVisibility()
    if (!this.youtubeValue) {
      this.clearSelection()
    }
  }

  selectOption(event) {
    const selectedValue = event.target.value
    this.updateCustomDatetimeVisibility(selectedValue)
    this.clearError()
  }

  updateCustomDatetimeVisibility(selectedValue = null) {
    if (!this.hasCustomContainerTarget) return

    if (selectedValue === null) {
      const selected = this.radioOptionTargets.find(radio => radio.checked)
      selectedValue = selected?.value
    }

    const showCustom = selectedValue === 'custom'
    this.customContainerTarget.hidden = !showCustom

    if (!showCustom && this.hasCustomDatetimeTarget) {
      this.customDatetimeTarget.value = ''
    }

    if (this.hasCustomDatetimeTarget) {
      this.customDatetimeTarget.required = showCustom
    }
  }

  setMinDatetime() {
    if (!this.hasCustomDatetimeTarget || !this.endTimeValue) return

    try {
      const endDate = new Date(this.endTimeValue)
      const minDate = new Date(endDate.getTime() + this.minDatetimeBufferValue * 60 * 1000)
      this.customDatetimeTarget.min = this.formatDatetimeLocal(minDate)
    } catch (e) {
      console.error('Error parsing end time:', e)
    }
  }

  validateCustomDatetime(event) {
    if (!this.hasCustomDatetimeTarget) return

    const selected = this.radioOptionTargets.find(radio => radio.checked)
    if (selected?.value !== 'custom') return

    const customValue = this.customDatetimeTarget.value
    if (!customValue) {
      this.showError('Debes seleccionar una fecha y hora para el envio')
      event.preventDefault()
      return
    }

    const customDate = new Date(customValue)
    const endDate = new Date(this.endTimeValue)

    if (customDate <= endDate) {
      this.showError('La fecha de envio debe ser posterior a la hora de fin del webinar')
      event.preventDefault()
      return
    }

    this.clearError()
  }

  updateEndTime(event) {
    if (event.detail?.endTime) {
      this.endTimeValue = event.detail.endTime
      this.setMinDatetime()
    }
  }

  handleYoutubeDetection(event) {
    this.youtubeValue = event.detail?.isYoutube === true
  }

  // Private methods

  clearSelection() {
    this.radioOptionTargets.forEach(radio => {
      radio.checked = false
    })
    this.updateCustomDatetimeVisibility()
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.hidden = false
    }

    if (this.hasCustomDatetimeTarget) {
      this.customDatetimeTarget.classList.add('rnk-Forms_Input--error')
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ''
      this.errorMessageTarget.hidden = true
    }

    if (this.hasCustomDatetimeTarget) {
      this.customDatetimeTarget.classList.remove('rnk-Forms_Input--error')
    }
  }

  formatDatetimeLocal(date) {
    const pad = (n) => n.toString().padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }
}
